import React, { useState } from 'react';

const TranslatorApp = () => {
  const [inputText, setInputText] = useState('');
  const [direction, setDirection] = useState('man-to-woman');
  const [premium, setPremium] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // FIXED: Translation database - quotes are consistent now
  const translations = {
    'man-to-woman': {
      standard: {
        "I'm fine": "I'm actually upset but don't want to talk about it right now.",
        "whatever you want is fine": "I have a preference but I'm avoiding the responsibility of making a decision.",
        "I'll do it later": "I acknowledge this task but have not committed to a specific timeline.",
        "I was just thinking": "I've been dwelling on this for some time but am presenting it as a casual thought.",
        "nothing's wrong": "Something is definitely wrong, but I'm not ready to discuss this.",
        "I don't care": "I do care, but I'm trying to appear neutral."
      },
      premium: {
        "I'm fine": "I'm processing my emotions and need some space. When I'm ready to talk, I'll let you know.",
        "whatever you want is fine": "I'm experiencing decision fatigue and would appreciate you making this choice while considering my usual preferences.",
        "I'll do it later": "This task is on my mental list but not prioritized. I'd appreciate a gentle reminder tomorrow.",
        "I was just thinking": "This topic has been on my mind for several days, and I value your perspective on it.",
        "nothing's wrong": "I'm experiencing feelings I can't articulate yet. Please give me time to process.",
        "I don't care": "This situation triggers complex emotions for me that I'm still sorting through."
      }
    },
    'woman-to-man': {
      standard: {
        "if you want to": "I would like you to do this, but am phrasing it as optional.",
        "no, it's okay": "It's not okay, but I'm temporarily tabling this discussion.",
        "I'm just saying": "This is important to me and I would like acknowledgment.",
        "did you notice anything different": "I made a specific change I'm proud of and would like recognition.",
        "I'm fine": "I'm not fine, and we need to discuss this.",
        "do whatever you want": "The choice you make now will be remembered and potentially referenced later."
      },
      premium: {
        "if you want to": "This matters to me, and I'd appreciate you doing it. I'm phrasing it as optional to avoid seeming demanding.",
        "no, it's okay": "This has hurt my feelings, but I need time to articulate why. Let's revisit this conversation later.",
        "I'm just saying": "This topic is important to my sense of security and happiness. Please acknowledge my perspective.",
        "did you notice anything different": "I've made an effort that I'm proud of and seeking validation that my efforts are visible and appreciated.",
        "I'm fine": "I'm processing several emotions and need you to be patient while also showing you care.",
        "do whatever you want": "I have strong feelings about this choice but am conflicted about expressing them directly."
      }
    }
  };

  // Extract available categories
  const categories = [
    'all',
    'communication',
    'arguments',
    'logistics',
    'relationship',
    'social'
  ];

  // Get examples for the current direction and category
  const getExamples = () => {
    const directionData = translations[direction];
    return Object.keys(directionData.standard).slice(0, 4);
  };
  
  // FIXED: Improved search functionality
  const searchTranslations = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const results = [];
    const lowerQuery = query.toLowerCase().trim();
    
    Object.entries(translations).forEach(([dir, tierData]) => {
      Object.entries(tierData).forEach(([tier, phrases]) => {
        Object.entries(phrases).forEach(([phrase, translation]) => {
          if (phrase.toLowerCase().includes(lowerQuery)) {
            results.push({
              direction: dir,
              tier,
              phrase,
              translation
            });
          }
        });
      });
    });
    
    setSearchResults(results.slice(0, 5)); // Limit to 5 results
  };

  // FIXED: Improved translation handling
  const handleTranslate = () => {
    if (!inputText.trim()) return;
    
    setLoading(true);
    
    // Simulate processing delay
    setTimeout(() => {
      const text = inputText.toLowerCase().trim();
      const directionData = translations[direction];
      const mode = premium ? 'premium' : 'standard';
      
      // FIXED: Improved lookup mechanism
      let translatedText = null;
      
      // First try direct case-insensitive match
      const phrases = Object.keys(directionData[mode]);
      for (let phrase of phrases) {
        if (phrase.toLowerCase().trim() === text) {
          translatedText = directionData[mode][phrase];
          break;
        }
      }
      
      // If no direct match, try partial matches
      if (!translatedText) {
        for (let phrase of phrases) {
          if (text.includes(phrase.toLowerCase()) || phrase.toLowerCase().includes(text)) {
            translatedText = directionData[mode][phrase];
            break;
          }
        }
      }
      
      // If still no match, use default message
      if (!translatedText) {
        translatedText = premium ? 
          "Our advanced algorithms detect complex emotions here. Try being more direct?" :
          "Translation not found. Try one of our example phrases or upgrade to premium!";
      }
      
      setTranslation({
        original: inputText,
        translated: translatedText,
        subtext: premium ? "Emotional context analyzed with Mind Reading Extension™" : "Basic translation provided"
      });
      
      setLoading(false);
    }, 1000);
  };

  // FIXED: Example handler
  const handleExample = (example) => {
    setInputText(example);
    
    // Auto-translate after selecting example
    setLoading(true);
    
    setTimeout(() => {
      const mode = premium ? 'premium' : 'standard';
      const translatedText = translations[direction][mode][example];
      
      setTranslation({
        original: example,
        translated: translatedText || "Translation not found",
        subtext: premium ? "Emotional context analyzed with Mind Reading Extension™" : "Basic translation provided"
      });
      
      setLoading(false);
    }, 800);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-pink-50 to-blue-50 p-4">
      <header className="text-center mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent">
          The Universal MAN ↔ WOMAN Translator
        </h1>
        <p className="text-gray-600 mt-2">Because sometimes what's said isn't what's meant</p>
      </header>
      
      <div className="max-w-2xl mx-auto w-full bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Translator Tool</h2>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="text-blue-500 hover:text-blue-700 text-sm font-medium"
          >
            {showSearch ? 'Hide Search' : 'Search Database'}
          </button>
        </div>
        
        {showSearch && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search the translation database:
            </label>
            <div className="flex">
              <input
                type="text"
                onChange={(e) => searchTranslations(e.target.value)}
                className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Type to search phrases..."
              />
              <button
                className="bg-blue-100 text-blue-800 py-2 px-4 rounded-r-md hover:bg-blue-200"
              >
                Search
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-3 border-t pt-3">
                <p className="text-sm text-gray-500 mb-2">Search results:</p>
                <ul className="space-y-2">
                  {searchResults.map((result, idx) => (
                    <li key={idx} className="text-sm p-2 bg-white rounded border">
                      <div className="font-medium">{result.phrase}</div>
                      <div className="text-gray-600 text-xs mt-1">{result.translation}</div>
                      <div className="text-gray-400 text-xs mt-1">
                        {result.direction === 'man-to-woman' ? 'Man → Woman' : 'Woman → Man'} | 
                        {result.tier === 'premium' ? ' Premium' : ' Standard'}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Translation Direction</label>
            <select 
              value={direction} 
              onChange={(e) => {
                setDirection(e.target.value);
                setTranslation(null);
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="man-to-woman">Man → Woman</option>
              <option value="woman-to-man">Woman → Man</option>
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select 
              value={category} 
              onChange={(e) => {
                setCategory(e.target.value);
                setTranslation(null);
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center mt-4 md:mt-6">
            <div className="flex items-center h-5">
              <input
                id="premium"
                type="checkbox"
                checked={premium}
                onChange={() => setPremium(!premium)}
                className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="premium" className="font-medium text-gray-700">
                Mind Reading Extension™ 
              </label>
              <p className="text-gray-500">$19.99 value - Free Trial!</p>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enter text to translate:
          </label>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleTranslate()}
            className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Type what was said here..."
            rows="3"
          ></textarea>
        </div>
        
        <div className="flex justify-between mb-6">
          <button
            onClick={handleTranslate}
            disabled={loading || !inputText.trim()}
            className="bg-gradient-to-r from-pink-500 to-blue-500 text-white py-2 px-6 rounded-md hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? 
              'Translating...' : 
              `Translate ${direction === 'man-to-woman' ? 'Man → Woman' : 'Woman → Man'}`}
          </button>
          
          <button
            onClick={() => {
              setInputText('');
              setTranslation(null);
            }}
            className="text-gray-600 underline"
          >
            Clear
          </button>
        </div>
        
        <div className="mb-2">
          <p className="text-sm text-gray-600 mb-2">Try these examples:</p>
          <div className="flex flex-wrap gap-2">
            {getExamples().map((example, i) => (
              <button
                key={i}
                onClick={() => handleExample(example)}
                className="text-xs bg-gray-100 hover:bg-gray-200 rounded-full py-1 px-3 text-gray-700"
              >
                "{example}"
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {translation && (
        <div className="max-w-2xl mx-auto w-full bg-white rounded-lg shadow-lg overflow-hidden mb-8 transition-all">
          <div className="bg-gradient-to-r from-pink-500 to-blue-500 p-4 text-white font-medium">
            Translation Results
          </div>
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500">Originally Said:</h3>
              <p className="text-lg">"{translation.original}"</p>
            </div>
            
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-500">What It Likely Means:</h3>
              <p className="text-lg font-medium text-gray-800">"{translation.translated}"</p>
            </div>
            
            <div className="text-xs text-gray-500 italic">
              {translation.subtext}
            </div>
          </div>
        </div>
      )}
      
      <footer className="text-center text-gray-500 text-sm mt-auto pt-6">
        <p>* Results are for entertainment purposes. Real understanding comes from actual communication.</p>
        <p className="mt-1">&copy; {new Date().getFullYear()} Translator Inc. All rights reserved. Unauthorized copying prohibited.</p>
      </footer>
    </div>
  );
};

export default TranslatorApp;
